import React from 'react';
import { Box, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import EnlargedImageDialog from '../../../components/general/EnlargedImageDialog';
import ImageReviewCard, { IdentifiableCorrection } from './ImageReviewCard';
import { QcOutcome, ReviewImage, dedupeImagesByFile } from './imageMeta';

interface ReviewGalleryProps {
    /**
     * The visit's images. May arrive with duplicates — the eCRF records the same
     * photograph once per submission — and is deduplicated here rather than by the
     * caller, so no call site can forget.
     */
    images: ReviewImage[];
    /** Whether this reviewer holds `VIEW_IDENTIFIABLE_IMAGES`. */
    canViewIdentifiable: boolean;
    /** The visit is approved (LOCKED). */
    readOnly?: boolean;
    /** Corrections already applied, keyed by `encryptedFileUuid`. */
    corrections?: Record<string, IdentifiableCorrection>;
    /** Open the correction dialog for one image. Absent means the action is not offered. */
    onCorrectIdentifiable?: (encryptedFileUuid: string) => void;
    /** Re-run retrieval + decryption for one image. */
    onRetry?: (encryptedFileUuid: string) => void;
    /** Render the per-image assessment (SKIN-241; a status line since SKIN-784) inside each card. */
    renderAssessment?: (image: ReviewImage) => React.ReactNode;
    /**
     * SKIN-784 — open a clicked image somewhere of the caller's choosing (the QC lesion review
     * dialog) instead of the gallery's own enlarge view. Absent keeps the enlarge view.
     */
    onOpen?: (encryptedFileUuid: string) => void;
    /**
     * The reviewer's verdict for one image, which frames its card. Absent means there are
     * no verdicts to show — deliberately NOT a fallback to a field on the image, because a
     * second place to look is a second answer that can be staler than the first. The
     * container seeds this resolver with whatever the server last sent.
     */
    outcomeFor?: (image: ReviewImage) => QcOutcome | null;
}

/**
 * SKIN-240 — every photograph on a visit, as one gallery of image cards.
 *
 * Presentational: it receives images whose bytes the decrypt pipeline has already
 * resolved (or failed to), and reports intent upward. It owns exactly two things the
 * caller should not have to repeat — deduplicating by photograph, and driving the shared
 * enlarge dialog.
 *
 * Each card renders whatever state its own `ReviewImage` carries, so a producer that
 * resolves images independently gets a gallery that fills in progressively rather than
 * one that blocks on the slowest image in the visit. That matters because a visit
 * averages 20.7 images and there is no embedded thumbnail to fall back on, so every one
 * of them is a full S3 fetch plus an AES-GCM decrypt plus a codec decode.
 *
 * No such producer exists yet. The shipped batch hook (`hooks/useDecryptedFiles.ts`)
 * resolves a whole set at once, so wired to that every card would flip together. The
 * per-image producer lands with the container in SKIN-256, composing the primitives that
 * module already exports (`importKekFromBase64`, `decryptFileFromS3`) the way
 * `ConsentViewer` composes them today.
 */
export default function ReviewGallery({
    images,
    canViewIdentifiable,
    readOnly = false,
    corrections,
    onCorrectIdentifiable,
    onRetry,
    renderAssessment,
    outcomeFor,
    onOpen,
}: ReviewGalleryProps) {
    // The image, not its URL. A copied blob: URL goes stale the moment the decrypt
    // pipeline revokes it — which is exactly what a retry does — leaving an open dialog
    // rendering a dead reference. Keying on the uuid makes the dialog follow the image:
    // it picks up the new URL after a retry and closes itself if the image stops being
    // showable.
    const [enlargedUuid, setEnlargedUuid] = React.useState<string | null>(null);

    // Deduplicate first, THEN number (below, at the render site). Numbering the raw
    // records would leave the reviewer with gaps ("1, 4, 5, 9") wherever a duplicate was
    // dropped, which reads as missing images rather than as records collapsed.
    const unique = React.useMemo(() => dedupeImagesByFile(images), [images]);

    const enlarged = unique.find((image) => image.encryptedFileUuid === enlargedUuid);
    const enlargedUrl = enlarged?.bytes.state === 'ready' ? enlarged.bytes.blobUrl : null;

    if (unique.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" data-testid="gallery-empty">
                <Translate id="hospital.quality-check.review.no-images" />
            </Typography>
        );
    }

    return (
        <Box data-testid="review-gallery" data-image-count={unique.length}>
            {/* alignItems matters: flex stretches children to the tallest sibling by
                default, so opening the categories on one image dragged every other card
                to the same height — four 835px columns, three of them holding a toggle
                and 600px of nothing. Each card now ends where its content ends. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                {unique.map((image, index) => (
                    <ImageReviewCard
                        key={image.encryptedFileUuid}
                        image={image}
                        number={index + 1}
                        outcome={outcomeFor?.(image) ?? null}
                        canViewIdentifiable={canViewIdentifiable}
                        readOnly={readOnly}
                        correction={corrections?.[image.encryptedFileUuid]}
                        onCorrectIdentifiable={onCorrectIdentifiable}
                        onRetry={onRetry}
                        onEnlarge={image.bytes.state === 'ready' ? (onOpen ?? setEnlargedUuid) : undefined}
                    >
                        {renderAssessment?.(image)}
                    </ImageReviewCard>
                ))}
            </Box>

            <EnlargedImageDialog url={enlargedUrl} onClose={() => setEnlargedUuid(null)} />
        </Box>
    );
}
